import { useCallback, useMemo } from 'react';
import DataSourceHelper from '../../../../helper/DataSourceHelper';
import useInterfaceEventBus from '../../../../hooks/useInterfaceEventBus';
import { getIsDataSourceAnEntityType } from '../../../../utils/entityDataSource';
import ApplicationDataSourceQueryNode from '../../components/ApplicationDataSourceQueryNode';
import {
  useDSCActions,
  useDSCDataSourceRecordStore,
  useDSCDependencyGraphProvider,
  useDSCGetState,
  useDSCGlobalStateStore,
  useDSCPageMetadata,
} from '../../context/selectors';
import type { ApplicationDataSourceQueryNodeProps, QueryNode } from '../../types';
import { getLookupRequestsForDataSources } from '../../utils/getLookupRequestsForDataSources';
import { createIdFromQueryKeyPrefix } from '../../utils/queryKeyPrefix';
import useManualDataSourceKeys from './useManualDataSourceTriggerIds';

function ManualDataSourceController() {
  const dataSources = useDSCDataSourceRecordStore().hooks.useDataSources();
  const metadata = useDSCPageMetadata();
  const dependencies = metadata?.dependencies;
  const dependencyById = metadata?.dependencyById;
  const {
    setManualDataSourceState,
    setDataSourceState,
    removeManualDataSourceState,
    removeManuallyTriggeredDataSources,
  } = useDSCActions();
  const { getState: getDSCState } = useDSCGlobalStateStore();
  const { triggerIds, requests } = useManualDataSourceKeys();
  const getDataSourceControllerState = useDSCGetState();
  const { interfaceEventBus } = useInterfaceEventBus();
  const { removeDataSource } = useDSCDataSourceRecordStore().hooks.useActions();
  const {
    actions: { notifyDependencyChange },
  } = useDSCDependencyGraphProvider();

  const onManuallyTriggeredDataSourceCompletion: ApplicationDataSourceQueryNodeProps['onQueryCompletion'] =
    useCallback(
      ({ id, requestId, outputSchema }) => {
        const latestRequests = getDSCState().manuallyTriggeredDataSources;
        latestRequests[requestId].callback?.();
        if (getIsDataSourceAnEntityType(id)) {
          removeDataSource(id);
        }
        removeManualDataSourceState(requestId);
        removeManuallyTriggeredDataSources([requestId]);

        if (outputSchema) {
          interfaceEventBus.publish('onSaveDataSourceSchema', {
            dataSourceId: id,
            outputSchema,
          });
        }
      },
      [
        interfaceEventBus,
        removeDataSource,
        removeManualDataSourceState,
        removeManuallyTriggeredDataSources,
        getDSCState,
      ],
    );

  const lookupRequestsByDataSourceId = useMemo(
    () => getLookupRequestsForDataSources(dependencies, dependencyById),
    [dependencies, dependencyById],
  );

  const getCurrentQueryResult: QueryNode['getCurrentQueryResult'] = useCallback(
    ({ requestId }) => {
      return getDataSourceControllerState().manualDataSources[requestId];
    },
    [getDataSourceControllerState],
  );

  const onSetDataSourceState: ApplicationDataSourceQueryNodeProps['setDataSourceState'] =
    useCallback(
      ({ id, requestId, result }) => {
        // in manual data source we don't need the refetch function as they are to be called manually
        // so we are removing it from the result as we use it to filter out the data sources that need to be refetched
        // in RefetchDataSourceController.native.tsx
        const { refetch, ...rest } = result;
        // id is the where we would have stored the things in the global state store
        setDataSourceState(id, rest);
        // requestId is the key we would have stored the things in the manualDataSources entry in the global state store
        setManualDataSourceState(requestId, rest);

        notifyDependencyChange({
          id,
        });
      },
      [setDataSourceState, setManualDataSourceState, notifyDependencyChange],
    );

  return (
    <>
      {triggerIds.map((requestId) => {
        const manuallyTriggeredDSDetails = requests[requestId];
        const dataSource = dataSources[manuallyTriggeredDSDetails.dataSourceId];
        const lookupRequests =
          lookupRequestsByDataSourceId[manuallyTriggeredDSDetails.dataSourceId];

        const dependsOn = DataSourceHelper.getDependsOn(dataSource);
        const dynamicPaths = DataSourceHelper.getDynamicPaths(dataSource);

        if (!dataSource) {
          console.warn(`Data source with id ${manuallyTriggeredDSDetails.dataSourceId} not found`);
          return null;
        }

        const newId = createIdFromQueryKeyPrefix({
          originalId: manuallyTriggeredDSDetails.dataSourceId,
          queryKeyPrefix: manuallyTriggeredDSDetails.queryKeyPrefix,
        });

        if (DataSourceHelper.isApplicationTypeDataSource(dataSource)) {
          return (
            <ApplicationDataSourceQueryNode
              dataSource={dataSource.properties}
              dependsOn={dependsOn}
              dynamicPaths={dynamicPaths}
              getCurrentQueryResult={getCurrentQueryResult}
              id={newId}
              key={`manualDataSource-${requestId}`}
              lookupRequests={lookupRequests}
              onQueryCompletion={onManuallyTriggeredDataSourceCompletion}
              originalDataSourceId={
                dataSource.properties.standardDataSourceId ??
                manuallyTriggeredDSDetails.dataSourceId
              }
              refetchOnPayloadChange={false}
              requestId={requestId}
              setDataSourceState={onSetDataSourceState}
              version={dataSource.version}
            />
          );
        }
        return null;
      })}
    </>
  );
}

export default ManualDataSourceController;
